import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetGuestBookingGlobalSettingQuery,
  useUpdateGuestBookingGlobalSettingMutation,
} from '@/services/GuestBookingGovernanceService';
import { ActivityLogType } from '@/types/audit-logs.types';
import { useIsPlatformAdmin } from '@/utils/userRole';

const GUEST_BOOKING_GOVERNANCE_PERMISSION = 'guest_booking_governance_manage';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null) {
    const typedError = error as {
      data?: { message?: string };
      message?: string;
    };

    if (typedError.data?.message) {
      return typedError.data.message;
    }

    if (typedError.message) {
      return typedError.message;
    }
  }

  return fallback;
};

const GuestBookingGovernanceConfiguration = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const isPlatformAdmin = useIsPlatformAdmin();

  // Per-harbor overrides now live on the harbor's own detail page; this
  // platform-wide page is admin-only regardless of who else might hold the
  // permission key via Role Permissions.
  const canManage =
    isPlatformAdmin && hasPermission(GUEST_BOOKING_GOVERNANCE_PERMISSION);

  const { data: globalSettingData, isLoading: isGlobalLoading } =
    useGetGuestBookingGlobalSettingQuery(undefined, { skip: !canManage });

  const [updateGlobalSetting, { isLoading: isUpdatingGlobal }] =
    useUpdateGuestBookingGlobalSettingMutation();

  const [globalEnabled, setGlobalEnabled] = useState(false);

  useEffect(() => {
    setGlobalEnabled(globalSettingData?.data?.enabled ?? false);
  }, [globalSettingData]);

  const handleGlobalToggle = async (checked: boolean) => {
    const previous = globalEnabled;
    setGlobalEnabled(checked);
    try {
      await updateGlobalSetting({ enabled: checked }).unwrap();
      enqueueSnackbar('Guest booking setting saved.', {
        variant: 'success',
      });
    } catch (error: unknown) {
      setGlobalEnabled(previous);
      enqueueSnackbar(
        getErrorMessage(error, 'Failed to save the guest booking setting.'),
        { variant: 'error' },
      );
    }
  };

  if (!canManage) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Control whether unauthenticated guests can book a berth
                  without signing in, platform-wide or per harbor.
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500">
                You do not have permission to manage guest booking governance.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  const isGlobalBusy = isGlobalLoading || isUpdatingGlobal;

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="guest-booking-governance-heading" />
              <ToolbarDescription>
                Control whether unauthenticated guests can book a berth
                without signing in, platform-wide or per harbor.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>

          {isGlobalLoading ? (
            <div className="card">
              <div className="card-body text-center py-4 text-gray-500">
                Loading...
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Allow guest booking platform-wide</h2>
              </div>
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-600">
                    Default for harbors that inherit the platform setting.
                    Each harbor can still override this individually below.
                  </p>
                  <label
                    htmlFor="guest-booking-global-toggle"
                    className="flex items-center gap-3 cursor-pointer"
                    id="guest-booking-global-status"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {globalEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <div className="relative inline-flex items-center">
                      <input
                        id="guest-booking-global-toggle"
                        type="checkbox"
                        className="sr-only peer cursor-pointer"
                        checked={globalEnabled}
                        onChange={(e) => handleGlobalToggle(e.target.checked)}
                        disabled={isGlobalBusy || !canManage}
                        role="switch"
                        aria-checked={globalEnabled}
                        aria-labelledby="guest-booking-global-status"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 contrast:bg-gray-700 contrast:peer-checked:bg-primary"></div>
                    </div>
                  </label>
                </div>
                <div className="flex flex-col gap-2 border-t pt-4">
                  <Link
                    to={`/user-logs?activityType=${ActivityLogType.GUEST_BOOKING_GLOBAL_SETTING_UPDATED}`}
                    className="text-xs text-blue-700 contrast:text-blue-300 hover:underline inline-block"
                  >
                    View change history in Activity Logs
                  </Link>
                  <Link
                    to={`/user-logs?activityType=${ActivityLogType.GUEST_BOOKING_HARBOR_OVERRIDE_UPDATED}`}
                    className="text-xs text-blue-700 contrast:text-blue-300 hover:underline inline-block"
                  >
                    View harbor override history in Activity Logs
                  </Link>
                </div>
              </div>
            </div>
          )}
        </Container>
      )}
    </Fragment>
  );
};

export { GuestBookingGovernanceConfiguration };
